import _ from "lodash";
import { useEffect, useState, useCallback, useRef } from "react";

import { useFormik } from "formik";
import * as yup from "yup";
import scrollToErrors from "../../utils/scrollToErrors";

import { useHistory, useLocation, useParams } from "react-router";
import { Framework, PDCAStages } from "../../config/Config";
import { useNotification } from "../../context/notification";
import { apiRequest } from "../../utils/api";
import { getLocalValue } from "../../utils/auth";
import { specialCharacterRegex } from "../../utils/customValidation";

import dayjs from "dayjs";
import utc from "dayjs-plugin-utc";

import styles from "./SessionsGrowForm.module.scss";

import TextField from "@material-ui/core/TextField";
import Button from "../../components/Button";
import NotesSidebar from "../../components/NotesSidebar";
import AiPopover from "../../components/AiPopover";
import useNudgePopover from "../../hooks/useNudgePopover";
import usePiiGuard from "../../hooks/usePiiGuard";
import useCampaignCodes from "../../hooks/useCampaignCodes";
import useClientCompanyNames from "../../hooks/useClientCompanyNames";
import useSubFunctionalUnitNames from "../../hooks/useSubFunctionalUnitNames";
import PiiDetectionPanel from "../../components/PiiDetectionPanel";
import PageContent from "../../components/PageContent";
import Section from "../../components/Section";
import SectionTitle from "../../components/SectionTitle";
import SessionReminderCard from "../../components/SessionReminderCard";
import Stepper from "../../components/Stepper";
import SubjectMetricTargetInput from "../../components/SubjectMetricTargetInput";
import { DashboardLayout } from "../../layouts/DashboardLayout";
import AiInsightDiv from "../../components/AiInsightDiv";

import { useTranslation } from "react-i18next";
import GetSessionData from "../../services/getSessionData";
import AiBanner from "../../components/AiBanner";
import { useCampaign } from "../../context/campaign";
dayjs.extend(utc);

const normalSessionField = label =>
  yup
    .string(`Please add ${label} to the session`)
    .required(`${label} field is mandatory`)
    .matches(specialCharacterRegex, "Special Characters are not allowed");

// Main validation schema
export const validationSchema = yup.object().shape({
  goal: normalSessionField("Goal"),
  opportunity: normalSessionField("Opportunity"),
  way_forward: normalSessionField("Way Forward"),
  subjects: yup.array().when("isPDFlow", {
    is: false,
    then: yup
      .array()
      .of(
        yup.object().shape({
          reality_target: yup
            .number()
            .required("Please enter Target")
            .typeError("Target must be a number"),
        })
      )
      .required("Subjects are mandatory"),
    otherwise: yup.array().notRequired(),
  }),
  reality_optional: yup.string(),
});

//getUrlQueryString
const useQuery = () => {
  return new URLSearchParams(useLocation().search);
};

const GUARDED_FIELDS = [
  "goal",
  "reality_optional",
  "opportunity",
  "way_forward",
];

export function SessionsGrowForm() {
  const history = useHistory();
  const { id } = useParams();
  const { t } = useTranslation();
  const { addNotification } = useNotification();
  const [sessionDetails, setSessionDetails] = useState({}); // raw fetched data

  const [sessionData, setSessionData] = useState({});
  const [growFormDetails, setGrowFormDetails] = useState(null);
  const [sessionReviewDate, setSessionReviewDate] = useState("");
  const [sessionDuration, setSessionDuration] = useState(0);
  const [isLoading, setIsLoading] = useState(false);
  const [isEditMode, setIsEditMode] = useState(
    useQuery().get("edit") === "true" ? true : false
  );

  const [subjectList, setSubjectList] = useState([]);
  const [formState, setFormState] = useState({});

  const getMainSubjectId = () => {
    return subjectList?.[0]?.subject_id || null;
  };

  const [fieldFeedbackStates, setFieldFeedbackStates] = useState({
    goal: null,
    opportunity: null,
    way_forward: null,
    reality_optional: null,
  });

  const prevFieldValues = useRef({
    goal: "",
    opportunity: "",
    way_forward: "",
  });

  const fieldAnchorRefs = useRef({});
  const getFieldAnchor = fieldName => fieldAnchorRefs.current[fieldName];

  // Snapshot of the answers entered so far, sent with every nudge so the AI has
  // the full session context (COACH-1975). Kept in a ref so the getter stays
  // stable and reads the latest values at nudge time.
  const sessionAnswersRef = useRef({});
  const getSessionAnswers = useCallback(() => sessionAnswersRef.current, []);

  const {
    popoverAnchorEl,
    streamedText,
    nudgeType,
    streaming,
    error,
    isOpen,
    aiDisabled,
    manuallyDisabled,
    handleNudgeRequest,
    handleClosePopover,
    handleFeedback,
    handleRefresh,
    handleDisableAI,
    handleEnableAI,
  } = useNudgePopover(id, getMainSubjectId, undefined, getSessionAnswers);

  // AI Coaching pilot region gate: useNudgePopover already folds the region
  // gate into aiDisabled above (blocking requests/popover) and into
  // AiInsightDiv's own visibility. setIsAiCoachingEnabled (not the read) is
  // still needed here to feed this session's region into that shared state.
  // See alt-ai-coaching-pilot.md.
  const { setIsAiCoachingEnabled } = useCampaign();

  const campaignCodes = useCampaignCodes();
  const clientCompanyNames = useClientCompanyNames();
  const subFunctionalUnitNames = useSubFunctionalUnitNames();
  const piiGuard = usePiiGuard(
    id,
    "preparation",
    sessionData.coachee_name,
    sessionData.coach_name,
    campaignCodes,
    clientCompanyNames,
    subFunctionalUnitNames
  );

  const piiScannedRef = useRef({});

  // Known-value lists load async - earlier scans ran against empty arrays,
  // so force a re-scan of every guarded field once they're populated.
  useEffect(() => {
    piiScannedRef.current = {};
  }, [campaignCodes, clientCompanyNames]);

  // Scan values restored from a saved draft (via enableReinitialize), not
  // just ones the user typed and then blurred - handleFieldBlur alone never
  // sees a hydrated value since no blur event fires for it.
  useEffect(() => {
    GUARDED_FIELDS.forEach(fieldName => {
      const value = formState?.[fieldName];
      if (value?.trim() && piiScannedRef.current[fieldName] !== value) {
        piiScannedRef.current[fieldName] = value;
        piiGuard.checkField(fieldName, value);
      }
    });
  }, [formState, campaignCodes, clientCompanyNames, piiGuard]);

  // Keep the session-answers snapshot in sync with the GROW form fields.
  useEffect(() => {
    sessionAnswersRef.current = {
      goal: formState?.goal ?? "",
      reality_optional: formState?.reality_optional ?? "",
      opportunity: formState?.opportunity ?? "",
      way_forward: formState?.way_forward ?? "",
    };
  }, [formState]);

  const handleFieldBlur = useCallback(
    (fieldName, label, value, e) => {
      if (value?.trim() && value !== prevFieldValues.current[fieldName]) {
        const piiHits = piiGuard.checkField(fieldName, value);
        prevFieldValues.current[fieldName] = value;
        if (piiHits.length > 0) return;

        handleNudgeRequest(
          fieldName,
          label,
          value,
          getFieldAnchor(fieldName),
          [...subjectList],
          type =>
            setFieldFeedbackStates(prev => ({ ...prev, [fieldName]: type }))
        );
      }
    },
    [handleNudgeRequest, subjectList, piiGuard]
  );

  const handleAiInsightClick = useCallback(
    (fieldName, label, value, e) => {
      const piiHits = piiGuard.checkField(fieldName, value);
      prevFieldValues.current[fieldName] = value;
      if (piiHits.length > 0) return;

      handleNudgeRequest(
        fieldName,
        label,
        value,
        getFieldAnchor(fieldName),
        [...subjectList],
        type => setFieldFeedbackStates(prev => ({ ...prev, [fieldName]: type }))
      );
    },
    [handleNudgeRequest, subjectList, piiGuard]
  );

  const initialForm = {
    session_id: id,
    goal: "",
    opportunity: "",
    way_forward: "",
    duration: sessionDuration,
    created_by: getLocalValue("uid"),
    grow_form_subject_details: [],
    reality_optional: "",
  };
  const isPDFlow = sessionData?.framework === Framework.PD;

  const formik = useFormik({
    initialValues: {
      goal: formState?.goal || "",
      opportunity: formState?.opportunity || "",
      way_forward: formState?.way_forward || "",
      isSpecialSession: false,
      isPDFlow: isPDFlow,
      subjects: subjectList || [],
      reality_optional: formState?.reality_optional || "",
    },
    validationSchema: validationSchema,
    onSubmit: values => {
      console.log("Form submitted with values:", values);

      handleFormSubmit(values); // Pass form values to submit handler
    },
    enableReinitialize: true,
  });

  // Runs when the component mounts
  useEffect(() => {
    let localData;

    // Check if localStorage has data for the current session
    try {
      const raw = localStorage.getItem("sessionGrowFormData");
      const parsed = raw ? JSON.parse(raw) : null;

      if (!parsed || parsed.session_id !== id) {
        // Save new form data if it doesn't exist or the session_id doesn't match
        localStorage.setItem(
          "sessionGrowFormData",
          JSON.stringify(initialForm)
        );
        localData = initialForm;
      } else {
        localData = parsed;
      }
    } catch (e) {
      // Fallback if JSON is malformed
      localData = initialForm;
      localStorage.setItem("sessionGrowFormData", JSON.stringify(initialForm));
    }

    // Process subject details
    const tmpSubjects = (localData.grow_form_subject_details || []).map(
      item => ({
        subject_id: item?.subject_id,
        mtd_metric: item?.mtd_metric,
        reality_target: item?.reality_target,
        project_target_kpi: item?.project_target_kpi,
        result_metrics: item?.result_metrics,
        subject: item?.subject,
        sub_subject: item?.sub_subject,
      })
    );
    setSubjectList(tmpSubjects);

    // Set the form state with local data
    setFormState({
      session_id: localData?.id || id,
      goal: localData?.goal || "",
      opportunity: localData?.opportunity || "",
      way_forward: localData?.way_forward || "",
      duration: localData?.duration || sessionDuration,
      created_by: localData?.created_by || getLocalValue("uid"),
      grow_form_subject_details: localData?.grow_form_subject_details || [],
      reality_optional: localData?.reality_optional || "",
    });
  }, []);

  // Handle fetching session data and grow form details
  useEffect(() => {
    const fetchAllSessionData = async () => {
      setIsLoading(true);

      try {
        // Run all three requests in parallel
        const [sessionRes, growFormRes, reviewDateRes] = await Promise.all([
          GetSessionData(id),
          apiRequest.get(`/api/getGrowformDetails?session_id=${id}`),
          apiRequest.get(`/api/getSessionReviewDate?session_id=${id}`),
        ]);

        // --- 1. Handle session data ---
        if (sessionRes) {
          setSessionDetails(sessionRes);
          setSessionData(sessionRes.session);
          // AI Coaching pilot region gate: derived from this specific
          // session's campaign -> region, refreshed on every load of this
          // page (bookmark, refresh, or direct nav) so it doesn't depend on
          // CampaignList having run earlier in the browser session. See
          // alt-ai-coaching-pilot.md.
          setIsAiCoachingEnabled(Boolean(sessionRes.ai_coaching_enabled));
        } else {
          addNotification(
            `Could not get session details from id #${id}`,
            "error"
          );
        }

        // --- 2. Handle grow form data ---
        if (growFormRes.status === 200) {
          const data = growFormRes.data.data;
          setSubjectList(data.subject);
          setGrowFormDetails(data.grow_form_static_data);
        } else {
          addNotification(
            `Could not get grow form details from session id #${id} (Status code: ${growFormRes.status})`,
            "error"
          );
        }

        // --- 3. Handle review date ---
        const reviewDate =
          reviewDateRes?.data?.data?.current_review_date?.[0]?.review_datetime;

        if (reviewDate) {
          const formattedDate = dayjs(reviewDate).format("DD MMM YYYY");
          setSessionReviewDate(formattedDate);
        }
      } catch (error) {
        console.error("Error fetching session-related data:", error);
        addNotification(
          "Something went wrong while fetching session data.",
          "error"
        );
      } finally {
        setIsLoading(false);
      }
    };

    fetchAllSessionData();
  }, [id]);

  // Keep the subject list updated
  useEffect(() => {
    setSubjectList(sessionDetails.subject);
  }, [sessionDetails]);

  // Update grow form details in the form state and localStorage
  useEffect(() => {
    if (_.isEmpty(growFormDetails)) return;

    const fieldsToUpdate = ["goal", "opportunity", "way_forward"];

    const updatedFields = fieldsToUpdate.reduce((acc, key) => {
      acc[key] = growFormDetails[key];
      return acc;
    }, {});

    // Update form state
    setFormState(prev => ({ ...prev, ...updatedFields }));

    // Update localStorage
    const localData = JSON.parse(localStorage.sessionGrowFormData || "{}");
    const updatedLocalData = { ...localData, ...updatedFields };

    localStorage.setItem(
      "sessionGrowFormData",
      JSON.stringify(updatedLocalData)
    );
  }, [growFormDetails]);

  // Update subject list in the form state when it changes
  useEffect(() => {
    if (subjectList && subjectList.length > 0) {
      let newList = subjectList.map(item => {
        return {
          subject_id: item.subject_id,
          mtd_metric: item.mtd_metric,
          reality_target: item.reality_target,
          project_target_kpi: item.project_target_kpi,
          result_metrics: item.result_metrics,
          subject: item.subject,
          sub_subject: item.sub_subject,
        };
      });
      handleFormChange("grow_form_subject_details", newList);
    }
  }, [subjectList]);

  // Update session value to form
  useEffect(() => {
    setFormState(prev => ({ ...prev, duration: sessionDuration }));
  }, [sessionDuration]);

  // Allow edit mode if growFormDetails is present
  useEffect(() => {
    if (growFormDetails) {
      setIsEditMode(true);
    }
  }, [growFormDetails]);

  const resetFormValues = () => {
    localStorage.removeItem("sessionGrowFormData");
  };

  // helper method to handle form changes
  const handleFormChange = (key, value) => {
    let newFormState = _.cloneDeep(formState);
    newFormState[key] = value;
    setFormState(newFormState);
    localStorage.setItem(`sessionGrowFormData`, JSON.stringify(newFormState));
  };

  // get current session duration for the stepper
  const getTotalTime = time => {
    setSessionDuration(time);
  };

  const handleFormSubmit = () => {
    setIsLoading(true);
    const apiUrl = isEditMode
      ? "/api/editGrowFormDetails"
      : "/api/saveSessionFormDetails";
    let payload = formState;

    // Remove extra items from the payload
    payload.grow_form_subject_details.forEach((item, index) => {
      payload.grow_form_subject_details[index] = {
        subject_id: item.subject_id,
        reality_target: item.reality_target,
      };
    });

    if (isEditMode) {
      payload["duration"] = {
        duration: sessionDuration,
        session_phase: PDCAStages.PLAN,
      };
    }
    apiRequest
      .post(apiUrl, payload)
      .then(resp => {
        if (resp.status === 200) {
          const data = resp.data.data;
          setIsLoading(false);
          history.push(`/sessions/grow/details/${id}`);
          resetFormValues();
        }
      })
      .catch(error => {
        setIsLoading(false);

        addNotification(`${error}`, "error");
      })
      .finally(() => {});
  };

  return (
    <DashboardLayout
      isSessionScoped
      appBarContent={
        <>
          {sessionData && (
            <Stepper
              isPDFlow={sessionData?.framework === Framework.PD}
              phase={t("header.stepper.plan")}
              phaseLabel="Plan"
              sessionId={id}
              clockRun={true}
              getTotalTimeSpent={getTotalTime}
            />
          )}
        </>
      }
      rightSidebarContent={
        <NotesSidebar
          sessionID={id}
          isCoach={
            sessionData && sessionData.hasOwnProperty("coach_id")
              ? sessionData["coach_id"] === parseInt(getLocalValue("uid"))
              : undefined
          }
          accessible={true}
        />
      }
    >
      <div className={styles.pageWrapper}>
        <PageContent indent>
          <Section>
            {sessionData && (
              <>
                <>
                  <SectionTitle>
                    {t("growForm.planSessionBasedonGrowModel")}
                  </SectionTitle>
                  {manuallyDisabled && (
                    <AiBanner
                      aiDisabled
                      onEnable={() => {
                        handleEnableAI();
                        addNotification("AI assist is now on", "success");
                      }}
                    />
                  )}

                  {sessionData?.framework !== Framework.PD && (
                    <SessionReminderCard
                      plain
                      header={t("growForm.yourReviewSessionIsOn")}
                      date={sessionReviewDate}
                    />
                  )}
                </>
                <div className={styles.detailsContainer}>
                  <div className={styles.row}>
                    <div className={styles.rowLeft}>
                      <div className={styles.labelTitle}>
                        {t("growForm.goals")}
                      </div>
                      <div className={styles.labelDescription}>
                        <p>
                          {t("growForm.knowingYourCoachingSubjectsAndCreate")}
                        </p>
                        <p>{t("growForm.SMARTGolasIsAnEstablished")}</p>
                      </div>
                    </div>
                    <div className={styles.rowRight}>
                      <div
                        className={styles.fieldWithInsight}
                        ref={el => {
                          fieldAnchorRefs.current.goal = el;
                        }}
                      >
                        <TextField
                          className={styles.textarea}
                          multiline
                          rows={4}
                          name="goal"
                          inputProps={{ style: { resize: "both" } }}
                          value={formState["goal"]}
                          placeholder={t("growForm.comment")}
                          variant="outlined"
                          onChange={e => {
                            handleFormChange("goal", e.target.value);
                            formik.handleChange(e);
                          }}
                          onBlur={e =>
                            handleFieldBlur(
                              "goal",
                              t("growForm.goals"),
                              formState.goal,
                              e
                            )
                          }
                          error={
                            (formik.touched.goal &&
                              Boolean(formik.errors.goal)) ||
                            piiGuard.isBlocked("goal")
                          }
                          helperText={formik.touched.goal && formik.errors.goal}
                        />
                        {piiGuard.isBlocked("goal") && (
                          <PiiDetectionPanel
                            hits={piiGuard.getHits("goal")}
                            onOverride={(hit, reason) =>
                              piiGuard.overrideHit("goal", hit, reason)
                            }
                          />
                        )}
                        <AiInsightDiv
                          state={fieldFeedbackStates.goal}
                          onClick={e =>
                            handleAiInsightClick(
                              "goal",
                              t("growForm.goals"),
                              formState.goal,
                              e
                            )
                          }
                        />
                      </div>
                      <AiPopover
                        open={isOpen && !aiDisabled}
                        anchorEl={popoverAnchorEl}
                        onClose={handleClosePopover}
                        nudgeText={streamedText}
                        nudgeType={nudgeType}
                        streaming={streaming}
                        error={error}
                        onFeedback={handleFeedback}
                        onRefresh={handleRefresh}
                        onCloseError={handleClosePopover}
                        onDisableAI={handleDisableAI}
                      />
                    </div>
                  </div>
                  <div className={styles.row}>
                    <div className={styles.rowLeft}>
                      <div className={styles.labelTitle}>
                        {t("growForm.reality")}
                      </div>
                      <div className={styles.labelDescription}>
                        <p>{t("growForm.knowingYourCurrentPerformance")}</p>
                      </div>
                    </div>
                    <div className={styles.rowRight}>
                      <SubjectMetricTargetInput
                        list={
                          subjectList && subjectList.length > 0
                            ? subjectList
                            : []
                        }
                        setList={setSubjectList}
                        formValidation={formik}
                        hideProjectTargetKpi={isPDFlow}
                        hideCurrentMetrics={isPDFlow}
                        hideTargetMetrics={isPDFlow}
                        sessionId={id}
                        aiDisabled={aiDisabled}
                        handleDisableAI={handleDisableAI}
                        handleNudgeRequest={handleNudgeRequest}
                        popoverAnchorEl={popoverAnchorEl}
                        isOpen={isOpen}
                        streamedText={streamedText}
                        nudgeType={nudgeType}
                        streaming={streaming}
                        error={error}
                        handleClosePopover={handleClosePopover}
                        handleFeedback={handleFeedback}
                        handleRefresh={handleRefresh}
                      />
                      <hr style={{ backgroundColor: "#BDBDBD" }} />
                      <div
                        className={`${styles.content} ${styles.fieldWithInsight}`}
                        ref={el => {
                          fieldAnchorRefs.current.reality_optional = el;
                        }}
                      >
                        <TextField
                          className={styles.textarea}
                          multiline
                          rows={5}
                          inputProps={{ style: { resize: "both" } }}
                          name="reality_optional"
                          value={formState["reality_optional"]}
                          placeholder={t("growForm.reality_optional")}
                          variant="outlined"
                          onChange={e => {
                            handleFormChange(
                              "reality_optional",
                              e.target.value
                            );
                            formik.handleChange(e);
                          }}
                          onBlur={e =>
                            handleFieldBlur(
                              "reality_optional",
                              t("growForm.reality"),
                              formState.reality_optional,
                              e
                            )
                          }
                          error={
                            (formik.touched.reality_optional &&
                              Boolean(formik.errors.reality_optional)) ||
                            piiGuard.isBlocked("reality_optional")
                          }
                          helperText={
                            formik.touched.reality_optional &&
                            formik.errors.reality_optional
                          }
                        />
                        {piiGuard.isBlocked("reality_optional") && (
                          <PiiDetectionPanel
                            hits={piiGuard.getHits("reality_optional")}
                            onOverride={(hit, reason) =>
                              piiGuard.overrideHit(
                                "reality_optional",
                                hit,
                                reason
                              )
                            }
                          />
                        )}
                        <AiInsightDiv
                          state={fieldFeedbackStates.reality_optional}
                          onClick={e =>
                            handleAiInsightClick(
                              "reality_optional",
                              t("growForm.reality"),
                              formState.reality_optional,
                              e
                            )
                          }
                        />
                      </div>
                    </div>
                  </div>
                  <div className={styles.row}>
                    <div className={styles.rowLeft}>
                      <div className={styles.labelTitle}>
                        {t("growForm.options")}
                      </div>
                      <div className={styles.labelDescription}>
                        <p>
                          {t("growForm.brainstormForMultipleOptionsToImprove")}
                        </p>
                      </div>
                    </div>
                    <div className={styles.rowRight}>
                      <div className={styles.contentContainer}>
                        <div
                          className={`${styles.content} ${styles.fieldWithInsight}`}
                          ref={el => {
                            fieldAnchorRefs.current.opportunity = el;
                          }}
                        >
                          <TextField
                            className={styles.textarea}
                            multiline
                            rows={4}
                            inputProps={{ style: { resize: "both" } }}
                            name="opportunity"
                            value={formState["opportunity"]}
                            placeholder={t("growForm.comment")}
                            variant="outlined"
                            onChange={e => {
                              handleFormChange("opportunity", e.target.value);
                              formik.handleChange(e);
                            }}
                            onBlur={e =>
                              handleFieldBlur(
                                "opportunity",
                                t("growForm.options"),
                                formState.opportunity,
                                e
                              )
                            }
                            error={
                              (formik.touched.opportunity &&
                                Boolean(formik.errors.opportunity)) ||
                              piiGuard.isBlocked("opportunity")
                            }
                            helperText={
                              formik.touched.opportunity &&
                              formik.errors.opportunity
                            }
                          />
                          {piiGuard.isBlocked("opportunity") && (
                            <PiiDetectionPanel
                              hits={piiGuard.getHits("opportunity")}
                              onOverride={(hit, reason) =>
                                piiGuard.overrideHit("opportunity", hit, reason)
                              }
                            />
                          )}
                          <AiInsightDiv
                            state={fieldFeedbackStates.opportunity}
                            onClick={e =>
                              handleAiInsightClick(
                                "opportunity",
                                t("growForm.options"),
                                formState.opportunity,
                                e
                              )
                            }
                          />
                        </div>
                      </div>
                    </div>
                  </div>
                  <div className={styles.row}>
                    <div className={styles.rowLeft}>
                      <div className={styles.labelTitle}>
                        {t("growForm.wayForward")}
                      </div>
                      <div className={styles.labelDescription}>
                        <p>{t("growForm.selectTheMostFeasibleOptions")}</p>
                      </div>
                    </div>
                    <div className={styles.rowRight}>
                      <div className={styles.contentContainer}>
                        <div
                          className={`${styles.content} ${styles.fieldWithInsight}`}
                          ref={el => {
                            fieldAnchorRefs.current.way_forward = el;
                          }}
                        >
                          <TextField
                            className={styles.textarea}
                            multiline
                            rows={4}
                            inputProps={{ style: { resize: "both" } }}
                            name="way_forward"
                            value={formState["way_forward"]}
                            placeholder={t("growForm.comment")}
                            variant="outlined"
                            onChange={e => {
                              handleFormChange("way_forward", e.target.value);
                              formik.handleChange(e);
                            }}
                            onBlur={e =>
                              handleFieldBlur(
                                "way_forward",
                                t("growForm.wayForward"),
                                formState.way_forward,
                                e
                              )
                            }
                            error={
                              (formik.touched.way_forward &&
                                Boolean(formik.errors.way_forward)) ||
                              piiGuard.isBlocked("way_forward")
                            }
                            helperText={
                              formik.touched.way_forward &&
                              formik.errors.way_forward
                            }
                          />
                          {piiGuard.isBlocked("way_forward") && (
                            <PiiDetectionPanel
                              hits={piiGuard.getHits("way_forward")}
                              onOverride={(hit, reason) =>
                                piiGuard.overrideHit("way_forward", hit, reason)
                              }
                            />
                          )}
                          <AiInsightDiv
                            state={fieldFeedbackStates.way_forward}
                            onClick={e =>
                              handleAiInsightClick(
                                "way_forward",
                                t("growForm.wayForward"),
                                formState.way_forward,
                                e
                              )
                            }
                          />
                          <AiPopover
                            open={isOpen && !aiDisabled}
                            anchorEl={popoverAnchorEl}
                            onClose={handleClosePopover}
                            nudgeText={streamedText}
                            nudgeType={nudgeType}
                            streaming={streaming}
                            error={error}
                            onFeedback={handleFeedback}
                            onRefresh={handleRefresh}
                            onCloseError={handleClosePopover}
                            onDisableAI={handleDisableAI}
                          />
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </>
            )}

            <div className={styles.detailsContainer}>
              <div className={styles.confirmButtonWrapper}>
                <Button
                  variant="contained"
                  color={"primary"}
                  fullWidth
                  isloading={isLoading ? 1 : 0}
                  disabled={piiGuard.hasAnyBlocking}
                  onClick={() => {
                    formik.handleSubmit();
                    scrollToErrors(formik);
                  }}
                >
                  {t("growForm.saveAndConfirmSession")}
                </Button>
              </div>
            </div>
          </Section>
        </PageContent>
      </div>
    </DashboardLayout>
  );
}
